// SNAF Modules 1-5: Expanded detailed content
export const snafPart1Topics = [
  {
    id: 'snaf-intro',
    title: 'Getting Started',
    readingTime: 8,
    subtopics: [
      {
        id: 'snaf-ebook-help',
        title: 'eBook Help',
        readingTime: 5,
        content: `<h2>Using This eBook</h2>
<p>Welcome to the ServiceNow Administration Fundamentals (SNAF) eBook. This comprehensive guide takes you through 9 detailed modules covering everything a ServiceNow administrator needs to know — from navigating the modernized work experience to securing your instance and completing a capstone project that validates your skills.</p>

<h3>How This Training Is Structured</h3>
<p>The SNAF training follows a progressive learning path where each module builds upon the knowledge from previous modules:</p>
<ul>
<li><strong>Module 1 - The Modernized Work Experience:</strong> Start here. Covers the Next Experience interface, user access, and personas. Establishes the foundation for all subsequent modules.</li>
<li><strong>Module 2 - Explore the Power of the Platform:</strong> Broadens your understanding of ServiceNow's full capability set across IT, employee, customer, and creator workflows.</li>
<li><strong>Module 3 - Configure Applications for Business:</strong> Hands-on configuration of lists, forms, business rules, and advanced platform features. This is where you start actively configuring the platform.</li>
<li><strong>Module 4 - Manage Data:</strong> Data architecture, access control, import mechanisms, and the CMDB. Critical for maintaining a healthy instance.</li>
<li><strong>Module 5 - Configure Self Service:</strong> Service catalog design, portal configuration, and workflow automation. Focuses on enabling end-user self-service.</li>
<li><strong>Module 6 - Enable Productivity:</strong> Analytics, notifications, and productivity tools that make teams more effective.</li>
<li><strong>Module 7 - Package Enhancements for Testing:</strong> Update sets, migration between instances, and platform utilities. Essential for change management.</li>
<li><strong>Module 8 - Securing a ServiceNow Instance:</strong> Security best practices, the shared responsibility model, and the Security Center.</li>
<li><strong>Module 9 - Capstone Project:</strong> A comprehensive project that ties together everything you've learned.</li>
</ul>

<h3>Study Tips for Success</h3>
<ul>
<li><strong>Active Learning:</strong> Don't just read — practice every concept in your PDI. The exercises in each module are designed to reinforce the material.</li>
<li><strong>Take Notes:</strong> Use the bookmark feature to mark important sections. Create a personal cheat sheet of key concepts, navigation paths, and configuration steps.</li>
<li><strong>Knowledge Checks:</strong> Each module ends with a knowledge check quiz. Aim for 100% — if you miss questions, review those topics before proceeding.</li>
<li><strong>Pace Yourself:</strong> Plan to complete 1-2 modules per week with dedicated practice time. Rushing through without hands-on practice will not prepare you for real-world administration or the certification exam.</li>
<li><strong>Search Feature:</strong> Use Ctrl+K to quickly search for any topic across all modules when you need to reference earlier material.</li>
</ul>`
      },
      {
        id: 'snaf-lab-setup',
        title: 'SNAF Lab Update Sets (Optional)',
        readingTime: 3,
        content: `<h2>Lab Update Sets</h2>
<p>Some exercises in the SNAF modules reference pre-configured data and configurations that can be loaded into your Personal Developer Instance (PDI) using update sets. While optional, these update sets provide a standardized environment that makes it easier to follow along with specific exercise steps.</p>

<h3>What Are Update Sets?</h3>
<p>Update sets are ServiceNow's primary mechanism for packaging configuration changes and moving them between instances. They capture changes to tables, forms, business rules, workflows, UI policies, and other configuration elements — essentially everything except data records. Think of them as version-controlled packages of platform configuration.</p>

<p>For this course, lab update sets add:</p>
<ul>
<li>Sample data records (users, incidents, changes, knowledge articles)</li>
<li>Pre-configured forms and list views</li>
<li>Sample business rules and workflows</li>
<li>Test configurations referenced in specific exercises</li>
</ul>

<h3>How to Install a Lab Update Set</h3>
<ol>
<li><strong>Download:</strong> Obtain the update set XML files from your course resources or training coordinator</li>
<li><strong>Navigate:</strong> In your instance, go to <strong>System Update Sets > Retrieved Update Sets</strong></li>
<li><strong>Import:</strong> Click <strong>"Import Update Set from XML"</strong> and select the downloaded file</li>
<li><strong>Preview:</strong> Open the imported update set and click <strong>"Preview Update Set"</strong>. This checks for any conflicts with existing configurations in your instance. Review any conflicts carefully.</li>
<li><strong>Commit:</strong> If the preview is clean (no conflicts), click <strong>"Commit Update Set"</strong> to apply all the changes to your instance</li>
<li><strong>Verify:</strong> Navigate to the areas configured by the update set to confirm the changes were applied correctly</li>
</ol>

<h3>Important Notes</h3>
<ul>
<li>Lab update sets are <strong>optional</strong>. All concepts can be learned and practiced without them.</li>
<li>If your PDI has been heavily customized, update sets may conflict with existing configurations. Always preview before committing.</li>
<li>Update sets can be <strong>backed out</strong> (reversed) if needed, though this should be done carefully.</li>
<li>If you encounter issues, you can always request a fresh PDI from developer.servicenow.com.</li>
</ul>`
      }
    ]
  },
  {
    id: 'snaf-m1',
    title: 'Module 1: The Modernized Work Experience',
    readingTime: 30,
    subtopics: [
      {
        id: 'snaf-m1-intro',
        title: 'Course Introduction',
        readingTime: 6,
        content: `<h2>Module 1: The Modernized Work Experience</h2>
<p>This module provides a deep-dive into the ServiceNow user experience, building on what you learned in the Welcome section. Here, you'll go beyond basic navigation to understand the architectural decisions behind the Next Experience framework, how different user personas interact with the platform, and how administrators configure the work environment to maximize productivity for each role.</p>

<h3>Learning Objectives</h3>
<p>By the end of this module, you will be able to:</p>
<ul>
<li>Explain the architectural components of the Next Experience framework and how they differ from legacy UI</li>
<li>Navigate the ServiceNow platform efficiently using the All menu, favorites, history, workspaces, and keyboard shortcuts</li>
<li>Describe the role-based access control model including users, groups, roles, and access control lists</li>
<li>Explain common user personas (System Administrator, ITIL User, Approver, End User) and their typical interactions with the platform</li>
<li>Configure workspace layouts and user preferences for different roles</li>
<li>Use impersonation and session debugging for testing and troubleshooting</li>
</ul>

<h3>Why This Module Matters</h3>
<p>Understanding the work experience is not just about knowing where buttons are — it's about understanding <strong>how ServiceNow is designed to be used</strong> by different people in different roles. As an administrator, you'll configure the platform to provide the right experience for each user persona. A fulfillment agent needs a focused workspace with their queue and quick actions. A manager needs dashboards and approval interfaces. An end user needs a simple self-service portal. The modernized work experience provides the framework for all of these scenarios.</p>

<h3>Module Structure</h3>
<p>This module covers four main areas:</p>
<ol>
<li><strong>The Modernized Work Experience:</strong> Technical deep-dive into the Next Experience framework, components, and configuration</li>
<li><strong>User Access and Personas:</strong> Understanding role-based access control, user management, and common personas</li>
<li><strong>Hands-On Exploration:</strong> Practical exercises to configure and test the work experience</li>
<li><strong>Knowledge Check:</strong> Quiz to validate your understanding</li>
</ol>`
      },
      {
        id: 'snaf-m1-modern',
        title: 'The Modernized Work Experience',
        readingTime: 10,
        content: `<h2>The Modernized Work Experience — Deep Dive</h2>
<p>ServiceNow's Next Experience is more than a visual refresh — it's a fundamental re-architecture of how users interact with the platform. Understanding its components and configuration options is essential for administrators who need to tailor the experience for their organization.</p>

<h3>Next Experience Framework Architecture</h3>
<p>The Next Experience framework consists of several integrated components:</p>

<p><strong>1. App Shell</strong></p>
<p>The App Shell is the outermost container that hosts all Next Experience content. It provides the banner frame (top navigation), manages navigation state, handles authentication context, and coordinates communication between components. The App Shell uses a <strong>micro-frontend architecture</strong>, meaning different parts of the interface can be built and deployed independently while appearing as a unified experience.</p>

<p><strong>2. Unified Navigation</strong></p>
<p>Unified Navigation is the navigation system that replaces the classic Application Navigator. Key components include:</p>
<ul>
<li><strong>All Menu:</strong> The centralized navigation hub. It organizes applications into categories and provides type-ahead search. The All menu is configurable — administrators can add custom menu items, reorder categories, and control visibility based on roles.</li>
<li><strong>Favorites:</strong> Users can star any page, module, or record for quick access. Favorites can be organized into user-defined groups. Favorites are stored per-user and persist across sessions.</li>
<li><strong>History:</strong> Automatically tracks the last 30+ pages visited, with timestamp and title. History includes pages across all applications and workspaces.</li>
<li><strong>Contextual App Pill:</strong> When viewing specific applications, an app pill in the banner shows the current application context and provides quick access to related modules.</li>
</ul>

<p><strong>3. Workspaces</strong></p>
<p>Workspaces are configurable, role-specific environments that represent the next evolution beyond traditional forms and lists. Each workspace provides:</p>
<ul>
<li><strong>Dashboard Home:</strong> Customizable landing page with widgets showing KPIs, charts, and metrics relevant to the workspace role</li>
<li><strong>Multi-Tab Interface:</strong> Open multiple records simultaneously in tabs, similar to a web browser. This allows agents to work on several tasks without losing context.</li>
<li><strong>Contextual Side Panel:</strong> Shows related information (activity stream, knowledge articles, similar records) alongside the main content without navigating away</li>
<li><strong>List View Integration:</strong> Embedded lists within the workspace that can be filtered and sorted without leaving the workspace context</li>
<li><strong>AI-Powered Features:</strong> Agent Assist provides real-time suggestions for knowledge articles, similar incidents, and recommended actions based on the current record content</li>
</ul>

<p><strong>Available Workspaces (out-of-box):</strong></p>
<ul>
<li>ITSM Workspace — For IT service desk agents</li>
<li>CSM Workspace — For customer service representatives</li>
<li>HR Agent Workspace — For HR case management</li>
<li>ITOM Health — For IT operations monitoring</li>
<li>Security Incident Response — For security analysts</li>
<li>Admin Center — For system administrators</li>
</ul>

<p><strong>4. Seismic Design System</strong></p>
<p>Seismic is ServiceNow's design system that defines the visual language of Next Experience. It provides:</p>
<ul>
<li><strong>Typography:</strong> Consistent font families, sizes, and weights across the platform</li>
<li><strong>Color System:</strong> A carefully designed color palette with support for themes and accessibility</li>
<li><strong>Components:</strong> Reusable UI components (buttons, inputs, cards, modals) that maintain consistency</li>
<li><strong>Spacing System:</strong> Consistent spacing and layout rules for visual harmony</li>
<li><strong>Accessibility:</strong> WCAG 2.1 AA compliance built into the design system</li>
</ul>

<h3>Configuring the Work Experience</h3>
<p>Administrators can customize the work experience through several mechanisms:</p>
<ul>
<li><strong>UI Builder:</strong> Visual tool for building custom workspace pages and components</li>
<li><strong>Workspace Configuration:</strong> Define landing pages, list configurations, and form views per workspace</li>
<li><strong>Theme Configuration:</strong> Customize colors, branding, and visual elements</li>
<li><strong>Banner Configuration:</strong> Control which banner elements are visible and their order</li>
<li><strong>Navigation Configuration:</strong> Manage menu items, categories, and module visibility</li>
</ul>`
      },
      {
        id: 'snaf-m1-access',
        title: 'User Access and Personas',
        readingTime: 8,
        content: `<h2>User Access and Personas</h2>
<p>ServiceNow implements a comprehensive <strong>role-based access control (RBAC)</strong> model that governs what each user can see, access, and modify. Understanding this model is critical for security, compliance, and providing appropriate user experiences. As an administrator, you will regularly manage users, groups, roles, and access controls.</p>

<h3>Access Control Building Blocks</h3>

<p><strong>1. Users [sys_user]</strong></p>
<p>User records represent individuals who interact with the ServiceNow platform. Key user record fields include:</p>
<ul>
<li><strong>User ID:</strong> Unique login identifier (often matches Active Directory/LDAP username)</li>
<li><strong>Email:</strong> Used for notifications and as an alternative login</li>
<li><strong>Active:</strong> Controls whether the user can log in (deactivating instead of deleting preserves audit trails)</li>
<li><strong>Source:</strong> Indicates how the user was created (LDAP, SAML, manual, import)</li>
<li><strong>Department, Location, Company:</strong> Organizational attributes used in assignment rules and access controls</li>
<li><strong>Manager:</strong> Reference to the user's manager (used in approval workflows)</li>
<li><strong>VIP:</strong> Flag for high-priority users that can trigger special handling rules</li>
<li><strong>Time Zone:</strong> Controls timestamp display for the user</li>
</ul>

<p><strong>2. Groups [sys_user_group]</strong></p>
<p>Groups organize users into functional teams. They serve multiple purposes:</p>
<ul>
<li><strong>Assignment:</strong> Tasks can be assigned to groups rather than individuals. Group managers or dispatchers then assign to specific members.</li>
<li><strong>Role Assignment:</strong> Roles can be assigned to groups, automatically granting those roles to all group members. This is the recommended approach for scalable role management.</li>
<li><strong>Approval:</strong> Groups can be used as approvers in workflows (any member of the group can approve).</li>
<li><strong>Notification:</strong> Notifications can be sent to entire groups.</li>
</ul>
<p>Groups can contain sub-groups (group hierarchy) and can have managers, email addresses, and schedules associated with them.</p>

<p><strong>3. Roles [sys_user_role]</strong></p>
<p>Roles are the primary mechanism for granting permissions. Each role defines a set of capabilities:</p>
<ul>
<li><strong>admin:</strong> Full system access. This is the most powerful role and should be granted sparingly. It inherits all other roles.</li>
<li><strong>itil:</strong> Standard ITSM agent role. Provides access to incident, problem, change, and knowledge management modules.</li>
<li><strong>catalog_admin:</strong> Manages service catalog items, categories, and catalog configuration.</li>
<li><strong>knowledge_admin:</strong> Manages knowledge bases, articles, and knowledge workflows.</li>
<li><strong>report_admin:</strong> Creates and manages reports and dashboards.</li>
<li><strong>user_admin:</strong> Manages user accounts, groups, and role assignments.</li>
<li><strong>approval_admin:</strong> Manages approval processes and configurations.</li>
</ul>
<p>Roles can <strong>contain other roles</strong>, creating a hierarchy. For example, the 'itil' role might contain 'itil_admin' and 'sn_incident_write'. When a user is granted the parent role, they automatically receive all contained roles.</p>

<p><strong>4. Access Control Lists (ACLs) [sys_security_acl]</strong></p>
<p>ACLs are fine-grained rules that determine read/write/create/delete permissions at three levels:</p>
<ul>
<li><strong>Table-level ACLs:</strong> Control access to entire tables. Example: Only users with the 'itil' role can read the Incident table.</li>
<li><strong>Field-level ACLs:</strong> Control access to specific fields. Example: Only users with 'itil' role can write to the 'State' field on incidents.</li>
<li><strong>Row-level ACLs:</strong> Control access based on record content. Example: Agents can only see incidents assigned to their group. This uses conditions evaluated against each record.</li>
</ul>

<h3>Common User Personas</h3>
<p>ServiceNow is used by many different personas, each requiring a different level of access and a different interface experience:</p>
<ul>
<li><strong>System Administrator:</strong> Has the 'admin' role with full platform access. Responsible for configuration, customization, and maintenance. Uses the platform UI and Admin Center workspace.</li>
<li><strong>ITIL Agent:</strong> Has the 'itil' role. Works incidents, problems, and changes. Uses the ITSM Workspace or platform UI. Needs access to knowledge base, CMDB, and reporting.</li>
<li><strong>Fulfiller:</strong> Has specific fulfillment roles. Receives and completes catalog tasks and other work orders. May use a specialized workspace.</li>
<li><strong>Approver:</strong> Has the 'approver_user' role. Reviews and approves/rejects requests, changes, and other items requiring authorization. Often works from email notifications or the approval page.</li>
<li><strong>End User:</strong> Has minimal roles (typically just 'snc_internal'). Accesses the Service Portal for self-service, catalog requests, knowledge search, and incident reporting. Should not access the platform UI.</li>
<li><strong>Manager:</strong> Has team management roles. Accesses dashboards, approval queues, and team performance metrics. May use a manager workspace or dashboard.</li>
</ul>

<h3>The Principle of Least Privilege</h3>
<p>Always follow the <strong>principle of least privilege</strong>: grant users only the minimum permissions needed to perform their job functions. This reduces security risk, limits the impact of compromised accounts, and simplifies compliance auditing. Regularly review role assignments and remove unnecessary access.</p>`
      },
      {
        id: 'snaf-m1-explore',
        title: 'Explore the Modernized Work Experience',
        readingTime: 6,
        content: `<h2>Hands-On: Explore the Modernized Work Experience</h2>
<p>These exercises give you practical experience with workspace configuration, user impersonation, and session debugging — skills you'll use daily as an administrator.</p>

<h3>Exercise 1: Explore User Administration</h3>
<ol>
<li>Navigate to <strong>User Administration > Users</strong></li>
<li>Open any user record and examine the available fields</li>
<li>Scroll to the <strong>"Roles"</strong> related list to see which roles are assigned</li>
<li>Scroll to the <strong>"Groups"</strong> related list to see group memberships</li>
<li>Create a new test user: click "New," fill in User ID, First Name, Last Name, Email, and set Active to true</li>
<li>Assign the 'itil' role to the test user by clicking "Edit" in the Roles related list</li>
</ol>

<h3>Exercise 2: Impersonate a User</h3>
<ol>
<li>Click your <strong>user avatar</strong> in the banner frame and select <strong>"Impersonate User"</strong></li>
<li>Search for and select your test user (or any non-admin user)</li>
<li>Notice how the interface changes — modules, menu items, and features visible to you are now limited to what that user's roles allow</li>
<li>Try accessing admin-only modules — you should receive "Insufficient privileges" errors</li>
<li>Navigate to the Service Portal to see the end-user experience</li>
<li>Click your avatar again and select <strong>"End Impersonation"</strong> to return to your admin session</li>
<li>Impersonation is a critical tool for testing access controls and verifying user experiences</li>
</ol>

<h3>Exercise 3: Explore the Admin Center</h3>
<ol>
<li>Navigate to the <strong>Admin Center</strong> workspace (search for it in the All menu)</li>
<li>Explore the available sections: Health, Usage, Performance, and Settings</li>
<li>Review the Instance Health metrics — these provide insights into your instance's configuration quality</li>
<li>Check the Usage section to see active user counts and login patterns</li>
</ol>

<h3>Exercise 4: Session Debugging</h3>
<ol>
<li>Navigate to <strong>System Diagnostics > Session Debug</strong> (or search "Debug")</li>
<li>Enable <strong>"Debug Security Rules"</strong> — this shows ACL evaluation details in the system log</li>
<li>With security debugging enabled, try accessing a record and check the <strong>System Log</strong> (System Logs > All) to see which ACLs were evaluated and whether they passed or failed</li>
<li>Disable debugging when finished — leaving it on impacts performance</li>
</ol>

<h3>Key Takeaways</h3>
<p>The modernized work experience is designed to provide the right interface for each user persona. Key administrative skills: (1) use impersonation regularly to verify that role assignments provide appropriate access, (2) leverage workspaces to provide focused, productive environments for different roles, (3) use session debugging to troubleshoot access issues, and (4) always follow the principle of least privilege when assigning roles.</p>`
      }
    ],
    quiz: {
      id: 'snaf-m1-quiz',
      title: 'Module 1: Knowledge Check',
      questions: [
        { id: 'sm1q1', type: 'mcq', question: 'What design system is the Next Experience built on?', options: ['Material Design', 'Seismic', 'Fluent UI', 'Carbon'], correctAnswer: 1 },
        { id: 'sm1q2', type: 'truefalse', question: 'The "admin" role inherits and includes all other roles in ServiceNow.', correctAnswer: true },
        { id: 'sm1q3', type: 'mcq', question: 'Which component provides role-specific environments with multi-tab interfaces in Next Experience?', options: ['Dashboards', 'Portals', 'Workspaces', 'Modules'], correctAnswer: 2 },
        { id: 'sm1q4', type: 'truefalse', question: 'Groups in ServiceNow can only contain users, not other groups.', correctAnswer: false },
        { id: 'sm1q5', type: 'mcq', question: 'What is the recommended approach for managing role assignments at scale?', options: ['Assign roles directly to each user', 'Assign roles to groups and add users to groups', 'Use the admin role for everyone', 'Create custom roles per user'], correctAnswer: 1 },
      ]
    }
  },
  {
    id: 'snaf-m2',
    title: 'Module 2: Explore the Power of the ServiceNow Platform',
    readingTime: 14,
    subtopics: [
      {
        id: 'snaf-m2-power',
        title: 'Explore the Power of the ServiceNow Platform',
        readingTime: 14,
        content: `<h2>The Power of the ServiceNow Platform</h2>
<p>ServiceNow has evolved from an IT ticketing system into a comprehensive enterprise platform that touches virtually every department and function in an organization. Understanding the full scope of the platform's capabilities helps administrators make better decisions about how to leverage ServiceNow to solve business challenges beyond traditional ITSM.</p>

<h3>IT Workflows</h3>
<p>IT Workflows are ServiceNow's original strength and remain the most widely deployed capabilities:</p>

<p><strong>IT Service Management (ITSM)</strong></p>
<p>The core ITSM suite covers ITIL-aligned processes:</p>
<ul>
<li><strong>Incident Management:</strong> Restore normal service operation as quickly as possible while minimizing business impact. Includes major incident management for high-impact events.</li>
<li><strong>Problem Management:</strong> Identify root causes of recurring incidents and implement permanent fixes. Supports both reactive (post-incident) and proactive (trend-based) analysis.</li>
<li><strong>Change Management:</strong> Manage the lifecycle of all changes to minimize risk and disruption. Includes standard, normal, and emergency change types with configurable approval workflows and risk assessment.</li>
<li><strong>Request Management:</strong> Handle service requests from the Service Catalog with automated fulfillment workflows.</li>
<li><strong>Knowledge Management:</strong> Create, share, and maintain knowledge articles to enable self-service and accelerate resolution.</li>
</ul>

<p><strong>IT Operations Management (ITOM)</strong></p>
<ul>
<li><strong>Discovery:</strong> Automatically identify and map IT infrastructure (servers, applications, network devices) to populate the CMDB</li>
<li><strong>Event Management:</strong> Consolidate alerts from monitoring tools, correlate events, and create incidents automatically</li>
<li><strong>Cloud Management:</strong> Provision, manage, and govern cloud resources across multi-cloud environments</li>
<li><strong>Service Mapping:</strong> Automatically discover and map business service dependencies</li>
</ul>

<p><strong>IT Asset Management (ITAM)</strong></p>
<ul>
<li><strong>Software Asset Management:</strong> Track software licenses, usage, and compliance</li>
<li><strong>Hardware Asset Management:</strong> Manage the lifecycle of hardware assets from procurement to disposal</li>
<li><strong>Cloud Cost Management:</strong> Monitor and optimize cloud spending</li>
</ul>

<h3>Employee Workflows</h3>
<p>Employee Workflows extend the ServiceNow platform beyond IT to serve all employees:</p>
<ul>
<li><strong>HR Service Delivery:</strong> Employee onboarding/offboarding, HR case management, benefits inquiries, and employee lifecycle events — all through a self-service portal with automated workflows</li>
<li><strong>Workplace Service Delivery:</strong> Facility requests, office management, space planning, and visitor management</li>
<li><strong>Legal Service Delivery:</strong> Legal request management, contract management, and compliance tracking</li>
<li><strong>Employee Center:</strong> A unified portal aggregating services from all departments into a single employee experience</li>
</ul>

<h3>Customer Workflows</h3>
<ul>
<li><strong>Customer Service Management (CSM):</strong> Manage customer cases, proactive outreach, and customer communities. Extends beyond traditional help desks to include proactive customer care, self-service portals, and AI-assisted case management.</li>
<li><strong>Field Service Management:</strong> Schedule and dispatch field technicians, manage work orders, and track parts inventory</li>
<li><strong>Order Management:</strong> End-to-end order lifecycle management with catalog-based ordering</li>
</ul>

<h3>Creator Workflows</h3>
<p>Creator Workflows empower organizations to build custom solutions on the platform:</p>
<ul>
<li><strong>App Engine:</strong> Low-code/no-code application development with App Engine Studio. Create custom applications with tables, forms, workflows, and portals without traditional coding.</li>
<li><strong>Flow Designer:</strong> Visual workflow automation tool for building automated processes. Supports triggers, conditions, parallel execution, and integration with external systems.</li>
<li><strong>IntegrationHub:</strong> Pre-built connectors (spokes) for integrating with popular services like Slack, Microsoft Teams, Jira, Salesforce, SAP, and hundreds more. Enables bi-directional data flow between ServiceNow and external systems.</li>
<li><strong>ServiceNow Store:</strong> Marketplace for certified applications built by ServiceNow partners and the community.</li>
</ul>

<h3>AI and Machine Learning Capabilities</h3>
<p>ServiceNow integrates AI throughout the platform:</p>
<ul>
<li><strong>Predictive Intelligence:</strong> Machine learning models that auto-classify, auto-assign, and auto-prioritize records based on historical patterns</li>
<li><strong>Natural Language Understanding (NLU):</strong> Powers Virtual Agent's ability to understand user intent from free-text input</li>
<li><strong>Document Intelligence:</strong> Extract structured data from documents (invoices, contracts, forms) using AI</li>
<li><strong>Now Assist (Generative AI):</strong> ServiceNow's generative AI capabilities for summarizing records, generating knowledge articles, assisting code development, and providing conversational search</li>
</ul>

<h3>The Single Architecture Advantage</h3>
<p>What makes ServiceNow unique among enterprise platforms is that <strong>all of these capabilities run on a single platform</strong> with a shared data model, common workflow engine, unified security model, and consistent user experience. This eliminates the integration challenges, data silos, and inconsistent experiences that plague organizations using separate tools for each function.</p>`
      }
    ],
    quiz: {
      id: 'snaf-m2-quiz',
      title: 'Module 2: Knowledge Check',
      questions: [
        { id: 'sm2q1', type: 'mcq', question: 'Which ServiceNow tool provides low-code workflow automation?', options: ['Script Include', 'Flow Designer', 'Business Rule', 'GlideRecord'], correctAnswer: 1 },
        { id: 'sm2q2', type: 'truefalse', question: 'ServiceNow can only be used for IT Service Management workflows.', correctAnswer: false },
        { id: 'sm2q3', type: 'mcq', question: 'What technology does ServiceNow use for connecting to external systems with pre-built connectors?', options: ['SOAP API only', 'IntegrationHub', 'FTP Server', 'Direct Database Link'], correctAnswer: 1 },
        { id: 'sm2q4', type: 'truefalse', question: 'App Engine Studio allows building custom applications without traditional coding.', correctAnswer: true },
      ]
    }
  },
  {
    id: 'snaf-m3',
    title: 'Module 3: Configure Applications for Business',
    readingTime: 25,
    subtopics: [
      {
        id: 'snaf-m3-lists',
        title: 'Lists and Filters',
        readingTime: 8,
        content: `<h2>Advanced Lists and Filters Configuration</h2>
<p>As an administrator, configuring lists goes beyond personal use — you'll define how lists appear and behave for all users across the organization. This includes managing list layouts per role, configuring list controls, enabling inline editing, and setting system-wide list properties.</p>

<h3>List Layout Administration</h3>
<p>List layouts define which columns appear in list views. As an administrator, you manage these at multiple levels:</p>
<ul>
<li><strong>Default View:</strong> The standard list layout that all users see. Changes here affect everyone unless they have a personalized view.</li>
<li><strong>Named Views:</strong> Custom views for specific purposes (e.g., "Self Service" view for end users, "Assignment" view for dispatchers). Users can switch between views using the view selector.</li>
<li><strong>Role-Based Views:</strong> Automatically show different views based on the user's roles. ITIL users might see detailed technical columns while end users see simplified columns.</li>
</ul>

<p><strong>Configuring List Layouts:</strong></p>
<ol>
<li>Navigate to the target list (e.g., incident.list)</li>
<li>Right-click any column header > <strong>Configure > List Layout</strong></li>
<li>In the configuration dialog, you'll see Available and Selected fields</li>
<li>Move fields between panels and reorder them</li>
<li>Use the <strong>View</strong> selector to configure different named views</li>
<li>Click Save to apply</li>
</ol>

<h3>List Controls and Properties</h3>
<p>System properties control global list behavior. Key properties include:</p>
<ul>
<li><strong>glide.ui.list.page_size:</strong> Default number of records per page (default: 20)</li>
<li><strong>glide.ui.list_edit:</strong> Enable/disable inline list editing globally</li>
<li><strong>glide.ui.list.allow_access_to_deleted_records:</strong> Controls visibility of deleted records</li>
<li><strong>glide.ui.per_page:</strong> Controls the page size options shown to users</li>
</ul>

<h3>Advanced Filtering Techniques</h3>
<p>Beyond basic conditions, advanced filtering provides powerful data analysis capabilities:</p>
<ul>
<li><strong>Encoded Queries:</strong> Filter conditions expressed as strings (e.g., <code>priority=1^state=1^ORpriority=2^state=1</code>). These can be used in URL parameters, scripts, reports, and shared bookmarks.</li>
<li><strong>Relative Date Filters:</strong> Dynamic date conditions like "Last 7 days," "This month," or "Last quarter." These automatically update, making them perfect for saved filters and reports.</li>
<li><strong>Dot-Walking:</strong> Filter on related table fields by traversing reference relationships. Example: <code>assigned_to.department.name=IT</code> filters records where the assigned user's department is "IT."</li>
<li><strong>BETWEEN Operator:</strong> Filter numeric or date fields within a range</li>
<li><strong>SAMEAS Operator:</strong> Compare two fields on the same record (e.g., "Opened by SAMEAS Assigned to" finds records where the creator is also the assignee)</li>
<li><strong>DYNAMIC Operator:</strong> Use dynamic values like "Current user," "Current user's groups," or "Current user's department" for personalized filters</li>
</ul>

<h3>List Performance Optimization</h3>
<p>Large lists with complex filters can impact performance. Best practices:</p>
<ul>
<li>Add database indexes to frequently filtered fields</li>
<li>Avoid dot-walking through multiple levels when possible</li>
<li>Use appropriate page sizes (20-50 records is optimal)</li>
<li>Configure list columns to show only needed fields (each column is a database query)</li>
<li>Use encoded queries in scripts instead of multiple addQuery() calls</li>
</ul>`
      },
      {
        id: 'snaf-m3-forms',
        title: 'Form Configuration',
        readingTime: 9,
        content: `<h2>Advanced Form Configuration</h2>
<p>Form configuration is one of the most common and impactful administrator tasks. Well-configured forms improve data quality, user productivity, and process compliance. Poorly configured forms frustrate users, lead to incomplete data, and increase support requests.</p>

<h3>Form Design Principles</h3>
<p>Before diving into technical configuration, understand these design principles:</p>
<ul>
<li><strong>Progressive Disclosure:</strong> Show only the fields relevant to the current stage of the record's lifecycle. New incidents don't need resolution fields visible.</li>
<li><strong>Logical Grouping:</strong> Organize fields into meaningful sections (Contact Info, Issue Details, Resolution, etc.)</li>
<li><strong>Visual Hierarchy:</strong> Place the most important fields prominently at the top. Secondary details go in lower sections.</li>
<li><strong>Minimal Friction:</strong> Reduce the number of mandatory fields to what's truly necessary. Every mandatory field is a potential point of friction.</li>
<li><strong>Role-Appropriate Views:</strong> Create different form views for different roles. End users submitting incidents need a simpler form than ITIL agents working incidents.</li>
</ul>

<h3>Form Configuration Tools</h3>

<p><strong>1. Form Designer (Recommended)</strong></p>
<p>The Form Designer provides a drag-and-drop interface for building form layouts:</p>
<ul>
<li>Access via: Right-click form header > Configure > Form Design (or System UI > Form Designer)</li>
<li>Drag fields from the palette to the form canvas</li>
<li>Create and organize sections with headers and separators</li>
<li>Configure multi-column layouts within sections</li>
<li>Set field properties: mandatory, read-only, visible</li>
<li>Add annotations (helper text) to guide users</li>
<li>Preview the form in different views</li>
</ul>

<p><strong>2. Form Layout (Quick Configuration)</strong></p>
<p>Form Layout provides a simpler interface for quick field arrangement:</p>
<ul>
<li>Access via: Right-click form header > Configure > Form Layout</li>
<li>Two-panel interface: Available fields / Selected fields</li>
<li>Faster than Form Designer for simple add/remove operations</li>
<li>Doesn't support section creation or multi-column layout</li>
</ul>

<h3>UI Policies (No-Code Behavior Control)</h3>
<p>UI Policies control field behavior on forms without requiring any scripting. They are the recommended approach for common field behavior requirements:</p>

<p><strong>UI Policy Configuration:</strong></p>
<ol>
<li>Navigate to <strong>System UI > UI Policies</strong></li>
<li>Click New and configure:
  <ul>
    <li><strong>Table:</strong> Which table this policy applies to</li>
    <li><strong>Condition:</strong> When the policy should activate (e.g., "State = Resolved")</li>
    <li><strong>Reverse if false:</strong> Whether to reverse the actions when conditions are no longer met</li>
    <li><strong>UI Policy Actions:</strong> What happens when the condition is true — set fields to mandatory, read-only, or visible/hidden</li>
  </ul>
</li>
</ol>

<p><strong>Common UI Policy Examples:</strong></p>
<ul>
<li>Make "Resolution Notes" mandatory when State changes to "Resolved"</li>
<li>Hide "Closure Code" section until the incident is in "Resolved" or "Closed" state</li>
<li>Make "Assignment Group" read-only after the incident is in "In Progress" state</li>
<li>Show "VIP-specific" fields only when the Caller is flagged as VIP</li>
</ul>

<h3>Client Scripts (Dynamic Form Behavior)</h3>
<p>When UI Policies aren't sufficient, Client Scripts provide JavaScript-based form control. Client Scripts run in the user's browser and come in four types:</p>
<ul>
<li><strong>onLoad:</strong> Executes when the form first loads. Use for setting initial values, showing messages, or configuring the form based on the record's current state.</li>
<li><strong>onChange:</strong> Executes when a specific field value changes. Use for field validation, auto-populating related fields, or dynamic visibility changes.</li>
<li><strong>onSubmit:</strong> Executes when the user clicks Save/Update/Submit. Use for form-level validation before the record is saved.</li>
<li><strong>onCellEdit:</strong> Executes during list inline editing. Use for validation during bulk list edits.</li>
</ul>

<h3>Data Policies (Server-Side Enforcement)</h3>
<p>Data Policies enforce data integrity at the server level, regardless of how the record is modified. Unlike UI Policies (client-side only), Data Policies apply to changes made through forms, APIs, import sets, workflows, and scripts. Use Data Policies when data integrity is critical and must be enforced universally.</p>`
      },
      {
        id: 'snaf-m3-advanced',
        title: 'Advanced Configuration',
        readingTime: 8,
        content: `<h2>Advanced Configuration Techniques</h2>
<p>Beyond forms and lists, ServiceNow provides a rich set of server-side configuration tools that automate business logic, enforce process rules, and handle complex workflows. These tools are the backbone of a well-configured ServiceNow instance.</p>

<h3>Business Rules</h3>
<p>Business Rules are server-side scripts that execute when records are displayed, inserted, updated, deleted, or queried. They are the most commonly used server-side automation tool in ServiceNow.</p>

<p><strong>Business Rule Types:</strong></p>
<ul>
<li><strong>Before Rules:</strong> Execute <em>before</em> the database operation. Use for:
  <ul>
    <li>Data validation (abort the operation if requirements aren't met)</li>
    <li>Setting field values (auto-populate fields before saving)</li>
    <li>Modifying the record before it's written to the database</li>
  </ul>
  Example: Before an incident is inserted, set Priority based on Impact and Urgency.
</li>
<li><strong>After Rules:</strong> Execute <em>after</em> the database operation completes. Use for:
  <ul>
    <li>Triggering notifications</li>
    <li>Creating or updating related records</li>
    <li>Logging activities</li>
  </ul>
  Example: After an incident's priority changes to Critical, send an email to the on-call manager.
</li>
<li><strong>Async (Asynchronous) Rules:</strong> Execute in the background after the operation. Use for non-time-sensitive processing that shouldn't delay the user's transaction. Example: After a change request is approved, trigger a complex integration with an external CMDB tool.</li>
<li><strong>Display Rules:</strong> Execute when a form is loaded (similar to onLoad Client Scripts but server-side). Use for setting initial field values or display conditions based on server-side data.</li>
</ul>

<p><strong>Business Rule Best Practices:</strong></p>
<ul>
<li>Always add conditions to limit when rules execute — unconditional rules run on every operation and impact performance</li>
<li>Use "before" rules for data manipulation, "after" rules for side effects</li>
<li>Avoid infinite loops by using <code>current.update()</code> carefully and checking <code>gs.isInteractive()</code></li>
<li>Keep business rule scripts focused — complex logic should be moved to Script Includes for reusability and testability</li>
</ul>

<h3>Script Includes</h3>
<p>Script Includes are reusable server-side JavaScript classes that encapsulate business logic. They promote code reuse, maintainability, and separation of concerns:</p>
<ul>
<li>Define utility functions used across multiple Business Rules</li>
<li>Implement complex calculations, validations, or integrations</li>
<li>Create REST API handlers for custom integrations</li>
<li>Build client-callable functions for Ajax requests from forms</li>
</ul>

<h3>Scheduled Jobs</h3>
<p>Scheduled Jobs run scripts at defined intervals for automated maintenance and processing:</p>
<ul>
<li><strong>Common Uses:</strong> Data cleanup (delete old attachments, archive closed records), SLA recalculation, automated report generation, data synchronization with external systems, compliance checks</li>
<li><strong>Configuration:</strong> Define the schedule (daily, weekly, monthly, or custom cron expression), the script to execute, and any conditions</li>
<li><strong>Monitoring:</strong> Check scheduled job execution logs for failures and performance issues</li>
</ul>

<h3>Notifications</h3>
<p>Notifications keep stakeholders informed about important events. They support email, SMS, push, and in-platform delivery:</p>
<ul>
<li><strong>Trigger Conditions:</strong> Record inserted, updated, or when specific field values change</li>
<li><strong>Recipients:</strong> Static users/groups, dynamic recipients (assigned to, opened by, group members), or scripted recipients</li>
<li><strong>Templates:</strong> HTML email templates with dynamic field values inserted using ${'$'}{field_name} notation. Templates support conditional blocks, related record data, and custom formatting.</li>
<li><strong>Digest Notifications:</strong> Combine multiple notifications into a single summary email to reduce inbox overload</li>
</ul>

<h3>System Properties</h3>
<p>System Properties are global configuration settings that control platform behavior. They're the "dials and switches" of the platform:</p>
<ul>
<li>Navigate to <strong>sys_properties.list</strong> to view all properties</li>
<li>Common categories: UI, email, security, performance, workflow, integration</li>
<li>Always document property changes and test in sub-production first</li>
<li>Some properties require instance restart to take effect</li>
</ul>`
      }
    ],
    quiz: {
      id: 'snaf-m3-quiz',
      title: 'Module 3: Knowledge Check',
      questions: [
        { id: 'sm3q1', type: 'mcq', question: 'What tool provides drag-and-drop form layout editing with section creation and multi-column support?', options: ['Form Layout', 'Form Designer', 'UI Builder', 'App Engine Studio'], correctAnswer: 1 },
        { id: 'sm3q2', type: 'truefalse', question: 'UI Policies require scripting to control field visibility on forms.', correctAnswer: false },
        { id: 'sm3q3', type: 'mcq', question: 'Which Business Rule type executes in the background for non-urgent processing?', options: ['Before', 'After', 'Async', 'Display'], correctAnswer: 2 },
        { id: 'sm3q4', type: 'truefalse', question: 'Data Policies enforce field requirements regardless of how a record is created — including via APIs and imports.', correctAnswer: true },
        { id: 'sm3q5', type: 'mcq', question: 'When should you use a "Before" Business Rule vs. an "After" Business Rule?', options: ['Before for notifications, After for validation', 'Before for data manipulation, After for side effects', 'They are interchangeable', 'Before for async, After for sync'], correctAnswer: 1 },
      ]
    }
  },
];
